import { DriverUser } from '../types/driverAuth';

const FACE_LIVENESS_TYPES = [
  'FACE_LIVENESS_RIGHT',
  'FACE_LIVENESS_LEFT',
  'FACE_LIVENESS_STRAIGHT',
] as const;
const PERSONAL_DOC_TYPES = ['ID_COPY', 'CRIMINAL_RECORD_CHECK', 'DRIVER_PHOTO'] as const;
const VEHICLE_PHOTO_TYPES = [
  'VEHICLE_PHOTO_FRONT',
  'VEHICLE_PHOTO_LEFT',
  'VEHICLE_PHOTO_RIGHT',
  'VEHICLE_PHOTO_BACK',
] as const;
const VEHICLE_DOC_TYPES = ['ROADWORTHY', 'LICENCE_DISK', 'PDP'] as const;

export type DriverRegisterFlowStage =
  | 'account'
  | 'driver-location'
  | 'verify-email'
  | 'phone-verification'
  | 'face-liveness'
  | 'personal-docs'
  | 'vehicle-details'
  | 'vehicle-photos'
  | 'account-type'
  | 'company-details'
  | 'bank-details'
  | 'submitted'
  | 'completed';

function hasDriverFaceLivenessPack(user: DriverUser): boolean {
  const attachments = user.attachments ?? {};
  const values = Object.values(attachments);

  return (
    values.some((attachment) =>
      FACE_LIVENESS_TYPES.includes(String(attachment?.fileType ?? '') as (typeof FACE_LIVENESS_TYPES)[number]),
    ) ||
    Object.keys(attachments).some((key) =>
      FACE_LIVENESS_TYPES.includes(key as (typeof FACE_LIVENESS_TYPES)[number]),
    )
  );
}

function hasAttachment(user: DriverUser, fileType: string): boolean {
  const attachments = user.attachments ?? {};
  const values = Object.values(attachments);
  return (
    Boolean(attachments[fileType]) ||
    values.some((attachment) => String(attachment?.fileType ?? '') === fileType)
  );
}

function hasAllAttachments(user: DriverUser, fileTypes: readonly string[]): boolean {
  return fileTypes.every((fileType) => hasAttachment(user, fileType));
}

function hasVehicleDetails(user: DriverUser): boolean {
  const vehicle = user.vehicles?.[0];
  return Boolean(
    vehicle?.vehicleType &&
      vehicle?.registrationNumber &&
      vehicle?.make &&
      vehicle?.model &&
      vehicle?.year &&
      vehicle?.vehicleColor,
  );
}

function inferDriverAccountType(user: DriverUser): DriverUser['accountType'] {
  if (user.accountType) {
    return user.accountType;
  }

  if (user.companyDetails?.companyName) {
    return 'COMPANY_DRIVER';
  }

  if (user.bankDetails?.bankName || hasAttachment(user, 'BANK_LETTER')) {
    return 'INDIVIDUAL_OWNER';
  }

  return null;
}

export function nextStageForDriver(user: DriverUser | null): DriverRegisterFlowStage {
  if (!user) return 'account';
  const effectiveAccountType = inferDriverAccountType(user);

  if (user.emailVerified === false) {
    return 'verify-email';
  }

  if (user.onboardingsStep === 'ACCOUNT_CREATED') {
    return user.emailVerified ? 'phone-verification' : 'verify-email';
  }

  if (user.onboardingsStep === 'EMAIL_VERIFIED') {
    return 'phone-verification';
  }

  if (!user.phoneVerified) {
    return 'phone-verification';
  }

  if (!hasDriverFaceLivenessPack(user)) {
    return 'face-liveness';
  }

  if (user.onboardingsStep === 'COMPLETED') {
    return 'completed';
  }

  if (user.onboardingsStep === 'SUBMITTED') {
    return 'submitted';
  }

  if (!hasAllAttachments(user, PERSONAL_DOC_TYPES)) {
    return 'personal-docs';
  }

  if (!hasVehicleDetails(user)) {
    return 'vehicle-details';
  }

  if (
    !hasAllAttachments(user, VEHICLE_PHOTO_TYPES) ||
    !hasAllAttachments(user, VEHICLE_DOC_TYPES)
  ) {
    return 'vehicle-photos';
  }

  if (!effectiveAccountType) {
    return 'account-type';
  }

  if (effectiveAccountType === 'COMPANY_DRIVER') {
    if (!user.companyDetails?.companyName) {
      return 'company-details';
    }
  }

  if (effectiveAccountType === 'INDIVIDUAL_OWNER') {
    if (!user.bankDetails?.bankName || !hasAttachment(user, 'BANK_LETTER')) {
      return 'bank-details';
    }
  }

  if (effectiveAccountType === 'COMPANY_DRIVER' || effectiveAccountType === 'INDIVIDUAL_OWNER') {
    return 'submitted';
  }

  if (user.onboardingsStep === 'COMPLETED') {
    return 'completed';
  }

  if (user.onboardingsStep === 'SUBMITTED') {
    return 'submitted';
  }

  return 'completed';
}
